import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { map, Observable, Subject, Subscription, take, takeUntil } from 'rxjs'
import { CommonModule, NgIf } from '@angular/common'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { RouterModule } from '@angular/router'
import { CapitalizePipe } from '../../pipes/capitalize.pipe'
import { BlockUnblockComponent } from '../template/block-unblock/block-unblock.component'
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
import { DeleteComponent } from '../template/delete/delete.component'
import { select, Store } from '@ngrx/store'
import { PaginationComponent } from '../template/pagination/pagination.component'
import { Business } from '../../store/business/business.state'
import { selectAllBusinesses } from '../../store/business/business.selectors'
import { loadBusinesses } from '../../store/business/business.actions'
import {
  blockDesignation,
  blockDesignationFailure,
  blockDesignationSuccess,
  createDesignation,
  createDesignationFailure,
  createDesignationSuccess,
  deleteDesignation,
  deleteDesignationFailure,
  deleteDesignationSuccess,
  loadDesignations,
  updateDesignation
} from '../../store/designation/designation.actions'
import { Designation } from '../../store/designation/designation.state'
import {
  selectDesignations,
  selectLoading,
  selectTotalDepartments
} from '../../store/designation/designation.reducer'
import { selectTotalCount } from '../../store/designation/designation.selectors'
import { selectDepartments } from '../../store/department/department.reducer'
import { Department } from '../../store/department/department.state'
import { loadDepartments } from '../../store/department/department.actions'
import { AuthState } from '../../store/auth/auth.state'
import { selectUser } from '../../store/auth/auth.selectors'
import { Actions, ofType } from '@ngrx/effects'
import { eps } from '../../services/url.service'
import { NgSelectModule } from '@ng-select/ng-select'
import { UtilsService } from '../../services/utils.service'
import { HasPermissionDirective } from '../../directives/has-permission.directive'
import { SharedModule } from '../../shared/shared.module'

@Component({
  selector: 'app-designation',
  imports: [
    ReactiveFormsModule,
    NgIf,
    CommonModule,
    RouterModule,
    CapitalizePipe,
    BlockUnblockComponent,
    FormsModule,
    DeleteComponent,
    PaginationComponent,
    NgSelectModule,
    HasPermissionDirective,
    SharedModule
    // BrowserAnimationsModule
  ],
  templateUrl: './designation.component.html',
  styleUrl: './designation.component.scss'
})
export class DesignationComponent implements OnInit {
  private store = inject(Store) // Inject store using `inject()`
  @ViewChild('addBusinessModals') addBusinessModals!: TemplateRef<any>
  private modalRef!: NgbModalRef
  currentPage = 1
  pageLimit = 10
  loading$: Observable<boolean> = this.store.select(state => state.designation.loading)
  error$: Observable<string | null> = this.store.select(state => state.designation.error)
  pageLastRecord = 0
  form!: FormGroup
  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)
  designations$: Observable<Designation[]> = this.store.select(selectDesignations)
  departments$: Observable<Department[]> = this.store.select(selectDepartments)
  totalCount$!: Observable<number>
  totalCount = 0
  search = ''
  blockDesignationId = ''
  showModal = false
  showDeleteModal = false
  isBlocking!: boolean // Assuming this will be set before opening the modal
  selectedBusinessId = ''
  user: AuthState['user'] | null = null
  businessIdss!: string[]
  title = ''
  status = false
  Math = Math
  isLoading$ = this.store.pipe(select(selectLoading))
  private destroyed$ = new Subject<void>()
  departmentCount$!: Observable<number>
  departmentCount = 0

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private actions$: Actions,
    private utilsService: UtilsService
  ) {
    // this.store.dispatch(loadBusinesses({ params: null }))
    this.designations$ = this.store.select(selectDesignations)
    this.totalCount$ = this.store.select(selectTotalCount).pipe(
      map(totalCount => totalCount || 0) // Default to 0 if totalCount is null
    )

    // Subscribe to totalCount$ safely
    this.totalCount$.subscribe(count => {
      this.totalCount = count // Define this.totalCount in your class
    })

    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe(user => {
        this.user = user
        const businessIds = user?.businessIds // your array of business IDs
        // const paramString = `ids=${businessIds?.join(',') ?? ''}`;
        if (businessIds && businessIds.length) {
          this.businessIdss = businessIds
          const paramString = `businessIds=${businessIds.join(',')}`
          this.store.dispatch(loadBusinesses({ params: paramString }))
        } else {
          this.loadAllBusinesses() // Load all businesses initially
          // this.loadAllBusinessOwners()
        }
      })
  }

  ngOnInit() {
    this.loadDesignations()

    this.totalCount$.subscribe(count => {
      this.totalCount = count
      this.updatePageLastRecord()
    })

    this.form = this.fb.group({
      designationName: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(100)]
      ], // Example validators
      departmentId: ['', Validators.required],
      businessId: ['', Validators.required]
      // businessOwnerId: ['']
    })

    this.actions$
      .pipe(
        ofType(
          createDesignationSuccess,
          createDesignationFailure,
          deleteDesignationFailure,
          deleteDesignationSuccess
        ),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.selectedBusinessId = ''
        this.search = ''
        this.status = false
        this.loadAllBusinesses()
        this.loadDesignations()
        this.blockDesignationId = ''
        this.form.reset()
        this.modalService.dismissAll() // or modalRef.close() if you're using one
      })

    this.businesses$.subscribe(businesses => {
      if (businesses.length === 1) {
        this.form.get('businessId')?.setValue(businesses[0].id)
        this.selectedBusinessId = businesses[0].id
        this.businessIdss = [businesses[0].id]
        this.onBusinessChange(businesses[0].id)
      }
    })

    this.departmentCount$ = this.store.select(selectTotalDepartments).pipe(
      map(departmentCount => departmentCount || 0) // Default to 0 if totalCount is null
    )
    this.departmentCount$.subscribe(count => {
      this.departmentCount = count // Define this.totalCount in your class
    })

    this.actions$
      .pipe(
        ofType(blockDesignationSuccess, blockDesignationFailure),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.loadDesignations()
        // this.form.reset()
        // this.modalService.dismissAll() // or modalRef.close() if you're using one
      })
  }

  loadDesignations() {
    const params: any = {}
    // if (this.businessIdss?.length) {
    //   params.businessIds = this.businessIdss.join(',')
    // }

    if (this.search) {
      params.designationName = this.search
    }

    if (this.selectedBusinessId) {
      params.businessId = this.selectedBusinessId
    }

    // if (this.status) {
    //   const isDisabled = this.status === 'true' ? true : this.status === 'false' ? false : null
    //   params.disabled = isDisabled
    // }

    params.disabled = this.status

    this.store.dispatch(
      loadDesignations({
        params: new URLSearchParams(params).toString(),
        page: this.currentPage,
        limit: this.pageLimit
      })
    )
  }

  onSearch() {
    this.currentPage = 1 // Reset to the first page on search
    const params = this.search ? `designationName=${encodeURIComponent(this.search)}` : null

    this.store.dispatch(loadDesignations({ params, page: this.currentPage, limit: this.pageLimit })) // Dispatch action with filter
  }

  onPageChange(newPage: number): void {
    if (newPage > 0) {
      this.currentPage = newPage
      this.loadDesignations() // Reload businesses for the new page
      this.updatePageLastRecord()
    }
  }

  private updatePageLastRecord() {
    this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
  }

  onPageLimitChange() {
    this.currentPage = 1 // Reset to first page when limit changes
    this.loadDesignations() // Load employees with new page limit
  }

  onBusinessSelect(businessId: string) {
    if (businessId) {
      // Call a method to fetch data based on the selected owner
      const params = `businessId=${encodeURIComponent(businessId)}&disabled=false`

      this.store.dispatch(
        loadDesignations({ params, page: this.currentPage, limit: this.pageLimit })
      ) // Dispatch action with filter
    } else {
      const params = ''
      this.store.dispatch(
        loadDesignations({ params, page: this.currentPage, limit: this.pageLimit })
      ) // Dispatch action with filter
      // Handle the case where no owner is selected
      // console.log('No business selected')
    }
  }

  onStatusSelect(status: string) {
    if (status) {
      // Call a method to fetch data based on the selected owner
      // const isDisabled = status === 'true' ? true : status === 'false' ? false : null

      // const params = isDisabled !== null ? `disabled=${isDisabled}` : ''
      const params = `disabled=${this.status}`

      this.store.dispatch(
        loadDesignations({ params, page: this.currentPage, limit: this.pageLimit })
      ) // Dispatch action with filter
    } else {
      const params = ''
      this.store.dispatch(
        loadDesignations({ params, page: this.currentPage, limit: this.pageLimit })
      ) // Dispatch action with filter
      // Handle the case where no owner is selected
      // console.log('No status selected
    }
  }

  openModal(isBlocking: boolean, designationId: string) {
    const modalRef = this.modalService.open(BlockUnblockComponent)
    modalRef.componentInstance.isBlocking = isBlocking
    modalRef.componentInstance.recordId = designationId
    // this.blockBusinessId = businessId

    modalRef.result.then(
      result => {
        // Handle the result from the modal
        if (result) {
          // Call the service to update the business status
          this.handleConfirm(result)
        }
      },
      reason => {
        // Handle the dismiss case
        if (reason) {
          this.handleConfirm(reason)
        }
      }
    )
  }

  handleConfirm(data: { id: string; status: boolean }) {
    this.store.dispatch(blockDesignation({ id: data.id, disabled: data.status }))
  }

  openDeleteModal(designationId: string) {
    const modalRef = this.modalService.open(DeleteComponent)
    modalRef.componentInstance.recordId = designationId
    modalRef.result.then(
      result => {
        // Handle the result from the modal
        if (result) {
          // Call the service to update the business status
          this.handleDeleteConfirm(result)
        }
      },
      reason => {
        // Handle the dismiss case
        if (reason) {
          this.handleDeleteConfirm(reason)
        }
      }
    )
  }

  handleDeleteConfirm(data: { id: string }) {
    this.store.dispatch(deleteDesignation({ id: data.id }))
  }

  onSubmit() {
    if (this.form.valid && !this.blockDesignationId) {
      this.store.dispatch(
        createDesignation({
          designationName: this.form.get('designationName')?.value,
          departmentId: this.form.get('departmentId')?.value,
          businessId: this.form.get('businessId')?.value
        })
      )
    } else if (this.form.valid && this.blockDesignationId) {
      this.store.dispatch(
        updateDesignation({
          id: this.blockDesignationId,
          designationName: this.form.get('designationName')?.value,
          departmentId: this.form.get('departmentId')?.value,
          businessId: this.form.get('businessId')?.value
        })
      )
    }
  }

  openBusinessModal(content: TemplateRef<any>, options: any = {}) {
    if (!content) {
      console.error('Modal template is undefined!')
      return
    }

    this.modalRef = this.modalService.open(content, {
      scrollable: false,
      centered: true,
      size: 'lg',
      ...options
    })
    this.title = 'Add'
  }

  onClose() {
    // if (this.form) {
    //   this.form.reset(); // Reset the form if it is defined
    // }
    // this.modalService.dismissAll();
    // this.currentStep = 1;
    this.blockDesignationId = ''
    try {
      if (this.form) {
        this.form.reset()
      }
      this.store.dispatch(loadDepartments({ params: '' }))
      if (this.modalRef) {
        this.modalRef.close()
      }
      this.modalService.dismissAll()
    } catch (error) {
      console.error('Error closing modal:', error)
    }
  }
  // getErrorMessage(controlName: string): string | null {
  //   const control = this.form.get(controlName)
  //   // console.log('control name', control)
  //   if (control && control.touched && control.invalid) {
  //     if (control.hasError('required')) {
  //       return 'This field is required'
  //     }
  //   }
  //   return null // No error
  // }

  openEditDesignation(content: any, designation: any) {
    // this.EditDesignation = Designation
    this.title = 'Edit'
    this.blockDesignationId = designation.id
    this.form.get('designationName')?.setValue(designation.designationName)
    // this.form.get('businessId')?.setValue(designation.businessId)
    // this.form.get('departmentId')?.setValue(designation.departmentId)
    this.loadAllBusinesses()
    this.form.get('businessId')?.setValue(String(designation.businessId))
    this.onBusinessChange(designation.businessId)
    this.form.get('departmentId')?.setValue(String(designation.departmentId))

    this.modalService.open(content, { scrollable: false, centered: true, size: 'md' })
  }

  onBusinessChange(businessId: string) {
    this.form.get('departmentId')?.setValue('')
    if (businessId) {
      const control = this.form.get('businessId')
      control?.setValue(businessId)
      control?.markAsDirty()
      control?.markAsTouched()
      control?.updateValueAndValidity()

      this.selectedBusinessId = businessId
      // Call a method to fetch data based on the selected owner
      const params = `businessId=${encodeURIComponent(businessId)}&disabled=false`

      this.store.dispatch(loadDepartments({ params })) // Dispatch action with filter
    } else {
      const params = 'disabled=false'
      this.store.dispatch(loadDepartments({ params })) // Dispatch action with filter
      // Handle the case where no owner is selected
      // console.log('No business selected')
    }
  }

  loadAllBusinesses() {
    const params: any = {}
    if (this.businessIdss?.length) {
      params.businessIds = this.businessIdss.join(',')
    }
    params.disabled = false

    this.store.dispatch(
      loadBusinesses({
        params: new URLSearchParams(params).toString()
      })
    )
  }

  getDashboardRoute(): string {
    // return this.user?.role === 'Admin' ? eps.dashboardPath : eps.employeeDashboardPath
    // const isAdmin = this.user?.userRoles?.some(role => role === 'Admin')
    // return isAdmin ? eps.dashboardPath : eps.employeeDashboardPath

    return this.utilsService.getDashboardRoute()
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (control && control.touched && control.errors) {
      for (const errorName in control.errors) {
        if (this.errorMessages[errorName]) {
          return this.errorMessages[errorName](control.errors[errorName])
        }
      }
    }
    return null
  }

  private errorMessages: { [key: string]: (error: any) => string } = {
    required: () => 'This field is required',
    minlength: error => `Minimum length is ${error.requiredLength} characters`,
    maxlength: error => `Maximum length is ${error.requiredLength} characters`
    // Add more error types here as needed
  }

  customSearchFn = (term: string, item: any) => {
    const fullName = `${item.businessName}`.toLowerCase()
    return fullName.includes(term.toLowerCase())
  }

  customSearchFnDepartment = (term: string, item: any) => {
    const fullName = `${item.departmentName}`.toLowerCase()
    return fullName.includes(term.toLowerCase())
  }
}
